/**
 * S5 — the review screen. **The artifact G3 signs off.**
 *
 * A renderer, and nothing more. Every section, subtotal, delta, dollar figure and
 * swing flag on this screen is a string the backend computed (§ 2.1): unit cost is
 * BC's and reachable only under the VPS-only Writer credential, the thresholds are
 * committed config validated at startup, and the two swing tests have deliberately
 * different boundary semantics — Karla's quantity test is strict `>`, Kevin's cost
 * test is `>=`. A client that re-derived any of it would be a second implementation
 * of a ruling, free to disagree with the one the backend pins by tests. So this file
 * does no arithmetic at all.
 *
 * Four rules from earlier steps land here rather than being re-decided:
 *
 * - **The D4 gate holds.** Review refuses to open over unsynced edits, because it
 *   adds up what BC holds and would otherwise leave the manager's last entries out
 *   of the totals *and* the flags. Online-only for the same reason (§ 4.6).
 * - **Error text verbatim and first** (D2's routing). The AL blanks `ErrorText` on
 *   `Error -> InReview`, so the way back destroys the message; it is read first or
 *   lost.
 * - **Read-only for non-owners** (D-7). Entry locks to the responsible manager, and
 *   so does every action; a viewer sees the numbers and no buttons.
 * - **Cancel lives here** (D-6), owner-only and `Open`/`InReview`-only.
 *
 * **The confirm lives here now (D6), and it lives in its own file.** CLAUDE.md rule 3
 * says posting is always an explicit manager action behind this screen; the gesture
 * that sends `confirm: true` is `review/ConfirmPost.tsx` and nothing on *this* screen
 * sends anything. The two controls below the sections are the whole of the way
 * forward: submit for review, which makes a count postable and clears a failed one's
 * error text, and the confirm modal, which is the only thing in the app that posts.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { useMovedInBc, usePendingReceipts, useSessionReview, useSyncLine } from "../api/queries";
import { ApiError } from "../api/client";
import type { ReviewAdvisory, SessionReview } from "../api/types";
import { reviewReadiness } from "../offline/reconcile";
import { useDraftSync } from "../offline/useDraftSync";
import { useOnline } from "../offline/useOnline";
import { useSessionRecord } from "../offline/useSessionRecord";
import { PendingReceiptsPanel } from "../entry/PendingReceiptsPanel";
import { CancelSession } from "../review/CancelSession";
import { ConfirmPost } from "../review/ConfirmPost";
import { MovedInBcBanner, movedMarker } from "../review/MovedInBcBanner";
import { ReviewLineRow } from "../review/ReviewLineRow";
import { SubmitForReview } from "../review/SubmitForReview";
import { isEditable, mayReviewOthers } from "../sessions/rules";
import { PostFailure } from "./SessionFacts";
import type { SessionScreenProps } from "./SessionRoute";

function BackToCounting({ areaId, sessionId }: { areaId: string; sessionId: string }) {
  return (
    <p>
      <Link
        to={`/areas/${encodeURIComponent(areaId)}/sessions/${encodeURIComponent(sessionId)}/entry`}
      >
        ← Back to counting
      </Link>
    </p>
  );
}

/** Advisories, grouped so the ones the AL will refuse over are unmissable. */
function Advisories({ advisories }: { advisories: ReviewAdvisory[] }) {
  if (advisories.length === 0) return null;
  const blocking = advisories.filter((a) => a.expect_al_refusal);
  const notes = advisories.filter((a) => !a.expect_al_refusal);

  return (
    <>
      {blocking.length > 0 && (
        <div className="card error" data-testid="advisories-blocking">
          <h3>
            {blocking.length === 1
              ? "One thing will stop this posting"
              : `${blocking.length} things will stop this posting`}
          </h3>
          {blocking.map((advisory) => (
            <p key={advisory.code + (advisory.item_no ?? "")} data-testid={`advisory-${advisory.code}`}>
              {advisory.message}
            </p>
          ))}
          <p className="muted small">
            {/* Said plainly, because the app is guessing at the AL's verdict and must
                not sound like it is issuing one. */}
            Business Central checks all of this again when the count is posted, and it
            has the final say — this is what the app expects it to refuse.
          </p>
        </div>
      )}
      {notes.length > 0 && (
        <div className="card notice" data-testid="advisories-notes">
          {notes.map((advisory) => (
            <p key={advisory.code + (advisory.item_no ?? "")} data-testid={`advisory-${advisory.code}`}>
              {advisory.message}
            </p>
          ))}
        </div>
      )}
    </>
  );
}

function Totals({ review }: { review: SessionReview }) {
  return (
    <div className="card totals" data-testid="totals">
      <h3>Combined</h3>
      <dl className="facts">
        <div>
          <dt>Inventory value counted</dt>
          {/* The spec's "combined inventory dollar value": counted qty × BC unit
              cost, summed server-side. */}
          <dd className="big" data-testid="total-counted-value">
            ${review.totals.counted_value}
          </dd>
        </div>
        <div>
          <dt>Value of the adjustment</dt>
          <dd className="big" data-testid="total-delta-value">
            ${review.totals.delta_value}
          </dd>
        </div>
        <div>
          <dt>Lines counted</dt>
          <dd data-testid="total-line-count">{review.totals.line_count}</dd>
        </div>
        <div>
          <dt>Flagged for review</dt>
          <dd data-testid="total-flagged">{review.totals.flagged_lines}</dd>
        </div>
        <div>
          <dt>Left blank</dt>
          <dd data-testid="uncounted-count">{review.uncounted_item_count}</dd>
        </div>
      </dl>
      {review.uncounted_item_count > 0 && (
        <p className="muted small" data-testid="uncounted-note">
          {review.uncounted_item_count} item
          {review.uncounted_item_count === 1 ? "" : "s"} on this area's list{" "}
          {review.uncounted_item_count === 1 ? "was" : "were"} left blank, so Business
          Central will leave{" "}
          {review.uncounted_item_count === 1 ? "its" : "their"} inventory unchanged —
          blank is never counted as zero.
        </p>
      )}
    </div>
  );
}

export function Review({ session, areaId, mine, principal }: SessionScreenProps) {
  const online = useOnline();
  const local = useSessionRecord(session.session_id);
  const sync = useDraftSync(areaId, session, principal);
  const readiness = reviewReadiness(sync.pending, local.record);
  // Both stores must have answered before the gate can mean anything: `pending` is 0
  // while the draft store is still being read, and opening on that would show totals
  // missing the manager's last entries for as long as IndexedDB took to reply.
  const storesRead = sync.loaded && local.loaded;
  const gateOpen = online && storesRead && readiness.ready;
  const review = useSessionReview(areaId, session.session_id, gateOpen);
  // Warnings that sit above the post button and gate nothing (PHASE_pending-receipts-
  // warning.md): open documents BC has not received, and movement in BC since a line
  // was counted. Live reads on their own endpoints; neither blocks posting.
  const pending = usePendingReceipts(areaId, session.session_id, gateOpen);
  const moved = useMovedInBc(areaId, session.session_id, gateOpen);

  // --- Editing a quantity from the review screen (item 6) -------------------
  //
  // The same `PUT …/lines/{item_no}` the entry screen uses — no separate
  // "review override" field to drift from what actually posts. On success the
  // whole review payload is re-read, so every advisory and swing flag is
  // re-evaluated against the edited quantity by the same server code that
  // computed them the first time; this screen still does no arithmetic of its
  // own.
  const syncLine = useSyncLine(areaId, session.session_id);
  const [savingItem, setSavingItem] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const editableHere = mine && isEditable(session);
  // The reviewer exception, as the server reports it for this session. Changes
  // what this screen *offers* (the confirm) and what it *says*, never what the
  // owner-only controls below do.
  const reviewerHere = mayReviewOthers(session, principal);

  async function editLine(itemNo: string, value: string) {
    setSavingItem(itemNo);
    setLineErrors((prev) => {
      const next = { ...prev };
      delete next[itemNo];
      return next;
    });
    try {
      await syncLine.mutateAsync({ item_no: itemNo, counted_qty: value });
      await review.refetch();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : (err as Error).message;
      setLineErrors((prev) => ({ ...prev, [itemNo]: detail }));
    } finally {
      setSavingItem((current) => (current === itemNo ? null : current));
    }
  }

  // --- Business Central's own words, before ANY gate -----------------------
  //
  // Hoisted deliberately. The error text is information, not a total: it is
  // unaffected by unsynced drafts and by having no connection, and the AL **blanks
  // it** on `Error -> InReview`, so it is read here or lost. Behind the sync gate, a
  // manager with pending entries on a failed count would have been shown "not ready
  // to review" and never the reason their post failed.
  const failure = <PostFailure session={session} />;

  if (!online) {
    return (
      <section data-testid="review-offline">
        <h2>{session.area.display_name} — review</h2>
        {failure}
        <div className="card error">
          <p>
            <strong>The review screen needs a connection.</strong> It shows what
            Business Central holds on hand and what each item is worth as of the
            posting date, and neither can be answered from this iPad.
          </p>
          <p className="muted small">
            Counting works offline and everything typed is held safely. Come back here
            when there is signal.
          </p>
        </div>
        <BackToCounting areaId={areaId} sessionId={session.session_id} />
      </section>
    );
  }

  if (!storesRead) {
    return (
      <section>
        {failure}
        <p className="muted">Checking what this device is still holding…</p>
      </section>
    );
  }

  if (!readiness.ready) {
    return (
      <section data-testid="review-blocked">
        <h2>{session.area.display_name} — review</h2>
        {failure}
        <div className="card error">
          <p>
            <strong>Not ready to review yet.</strong> {readiness.message}
          </p>
          <button
            type="button"
            onClick={() => void sync.flush()}
            disabled={sync.flushing}
            data-testid="review-sync-now"
          >
            {sync.flushing ? "Syncing…" : `Sync ${readiness.pending} now`}
          </button>
          <p className="muted small">
            Last full sync: {local.record?.last_full_sync_at ?? "never on this device"}
          </p>
        </div>
        <BackToCounting areaId={areaId} sessionId={session.session_id} />
      </section>
    );
  }

  return (
    <section data-testid="review-screen">
      <h2>{session.area.display_name} — review</h2>

      {/* First on the screen, before any way forward, and verbatim. */}
      {failure}

      {reviewerHere && (
        <div className="card notice" data-testid="review-as-reviewer">
          <p>
            <strong>You are reviewing this count, not entering it.</strong>{" "}
            <code>{session.responsible_manager}</code> entered it. You can read every
            number and post it; changing quantities, submitting and cancelling stay with
            its owner.
          </p>
        </div>
      )}

      {!mine && !reviewerHere && (
        <div className="card" data-testid="review-read-only">
          <p>
            <strong>Read-only.</strong> <code>{session.responsible_manager}</code> is the
            responsible manager for this count. You can read every number on this
            screen; posting and cancelling lock to its owner, including for an admin.
          </p>
        </div>
      )}

      {review.isLoading && <p className="muted">Reading Business Central…</p>}

      {review.error && (
        <div className="card error" data-testid="review-error">
          <h3>Could not build the review</h3>
          <p>{(review.error as Error).message}</p>
          <p className="muted small">
            Nothing is wrong with the count itself — this screen reads on-hand and
            costs fresh from Business Central every time, and that read failed.
          </p>
          <button type="button" onClick={() => void review.refetch()}>
            Try again
          </button>
        </div>
      )}

      {review.data && (
        <>
          <div className="card review__header" data-testid="review-header">
            <dl className="facts">
              <div>
                <dt>Posting date</dt>
                {/* The date the deltas were computed against, which is the whole
                    basis of every number below. */}
                <dd data-testid="as-of">
                  <strong>{review.data.as_of}</strong>
                </dd>
              </div>
              <div>
                <dt>Area</dt>
                <dd>
                  {review.data.area.display_name} · {review.data.area.location_code}
                </dd>
              </div>
              <div>
                <dt>Responsible manager</dt>
                <dd>{session.responsible_manager}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{session.status}</dd>
              </div>
            </dl>
            <p className="muted small" data-testid="thresholds">
              {/* Served rather than hardcoded, so the screen can name the number it
                  applied — and so "which thresholds am I running" has an answer. */}
              Flagged when the adjustment is more than{" "}
              <strong>{review.data.thresholds.quantity_variance_percent}%</strong> of
              what Business Central holds, or worth{" "}
              <strong>${review.data.thresholds.cost_variance}</strong> or more at BC
              unit cost ({review.data.thresholds.area_type} area,{" "}
              <code>{review.data.thresholds.source}</code>). Either one alone flags a
              line. <strong>Flags never block posting</strong> — they mark what to look
              at twice.
            </p>
          </div>

          {review.data.nothing_to_post && (
            <div className="card notice" data-testid="nothing-to-post">
              <p>
                <strong>Nothing to post.</strong> Every counted item matches Business
                Central, so posting would move no inventory.
              </p>
            </div>
          )}

          <Advisories advisories={review.data.advisories} />

          {review.data.warnings.length > 0 && (
            <div className="card notice small" data-testid="review-warnings">
              {review.data.warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          )}

          <Totals review={review.data} />

          {review.data.sections.length === 0 ? (
            <div className="card" data-testid="no-lines">
              <p>
                Nothing is staged for this count, so there is nothing to review. Every
                item on the list is blank — and blank means "not counted", never zero.
              </p>
            </div>
          ) : (
            review.data.sections.map((section) => (
              <section
                key={section.section ?? "unassigned"}
                className="card review__section"
                data-testid={`review-section-${section.section ?? "unassigned"}`}
              >
                <header className="review__section-head">
                  <h3>{section.display_name}</h3>
                  <dl className="facts">
                    <div>
                      <dt>Counted value</dt>
                      <dd data-testid={`section-counted-${section.section ?? "unassigned"}`}>
                        ${section.counted_value}
                      </dd>
                    </div>
                    <div>
                      <dt>Adjustment value</dt>
                      <dd data-testid={`section-delta-${section.section ?? "unassigned"}`}>
                        ${section.delta_value}
                      </dd>
                    </div>
                    <div>
                      <dt>Lines</dt>
                      <dd>{section.line_count}</dd>
                    </div>
                    <div>
                      <dt>Flagged</dt>
                      <dd>{section.flagged_lines}</dd>
                    </div>
                  </dl>
                </header>
                <div className="review__scroll">
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="num">Counted</th>
                        <th className="num">On hand</th>
                        <th className="num">Difference</th>
                        <th className="num">Counted value</th>
                        <th className="num">Adjustment</th>
                        <th>Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.lines.map((line) => (
                        <ReviewLineRow
                          key={line.item_no}
                          line={line}
                          editable={editableHere}
                          saving={savingItem === line.item_no}
                          error={lineErrors[line.item_no]}
                          movedNote={moved.data ? movedMarker(moved.data, line.item_no) : null}
                          onEdit={(itemNo, value) => void editLine(itemNo, value)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))
          )}

          {/* The way forward, at the bottom of the numbers rather than above them:
              a manager scrolls past what they are authorising to reach it. Exactly
              one of these renders — they key off statuses that cannot overlap — and
              both are owner-gated in their own files. */}
          <PendingReceiptsPanel
            data={pending.data}
            loading={pending.isLoading}
            error={pending.error ? (pending.error as Error).message : null}
            countedItems={new Set(session.lines.map((l) => l.item_no.trim().toUpperCase()))}
          />
          <MovedInBcBanner
            data={moved.data}
            error={moved.error ? (moved.error as Error).message : null}
          />
          <SubmitForReview session={session} areaId={areaId} mine={mine} />
          <ConfirmPost
            session={session}
            areaId={areaId}
            mine={mine}
            mayPost={reviewerHere}
            review={review.data}
          />
        </>
      )}

      <CancelSession session={session} areaId={areaId} mine={mine} />

      <BackToCounting areaId={areaId} sessionId={session.session_id} />
    </section>
  );
}
